<template>
  <div v-loading="loading">
    <div class="my-4 space-y-2 bg-brand-sand rounded-md shadow-sm px-4 py-4">
      <p class="text-brand-black font-medium">
        Please use
        <a href="https://docs.churnkey.co/braintree" target="_blank" class="underline text-brand-blue font-semibold">
          our guide for connecting your Braintree account </a
        >.
      </p>
      <p class="text-brand-brown text-sm font-medium">
        Your account specific webhook URL (for step 3):
        <span class="text-brand-blue font-semibold"> https://billing.churnkey.co/e/src_3owuSFmHG965?appId={{ $store.state.org.appId }} </span>
      </p>
    </div>
    <div class="space-y-6 divide-y-2 divide-brand-sand">
      <div>
        <div class="header flex items-center space-x-2">
          <p v-if="liveBadCreds" class="text-lg font-bold text-brand-red flex items-center space-x-2">
            <font-awesome-icon :icon="['fas', 'times-circle']" class="text-sm"></font-awesome-icon>
          </p>
          <p v-else-if="liveValidated" class="text-lg font-bold text-green-700 flex items-center space-x-2">
            <font-awesome-icon :icon="['fas', 'check-circle']" class="text-sm"></font-awesome-icon>
          </p>
          <p v-else class="text-lg font-bold text-green-700 flex items-center space-x-2">
            <font-awesome-layers class="text-xl">
              <font-awesome-icon :icon="['fas', 'circle']" class="text-brand-medium-blue" />
              <font-awesome-icon :icon="['fas', 'plug']" class="text-xs text-brand-blue" :class="'text-brand-blue'" />
            </font-awesome-layers>
          </p>
          <span> Production Mode </span>
        </div>
        <div class="grid grid-cols-2 gap-4 max-w-xl mt-2">
          <div>
            <p class="text-sm font-semibold text-gray-700 mb-2">Merchant ID</p>
            <el-input v-if="!editLive && liveCredentials.merchantId" :value="liveCredentials.merchantId" type="text" disabled></el-input>
            <el-input v-else v-model="liveMerchantId" type="text"></el-input>
          </div>
          <div>
            <p class="text-sm font-semibold text-gray-700 mb-2">Public Key</p>
            <el-input v-if="!editLive && liveCredentials.publicKey" :value="liveCredentials.publicKey" type="text" disabled></el-input>
            <el-input v-else v-model="livePublicKey" type="text"></el-input>
          </div>
          <div class="col-span-2">
            <p class="text-sm font-semibold text-gray-700 mb-2">Private Key</p>
            <el-input
              v-if="!editLive && liveCredentials.privateKey"
              :value="liveCredentials.privateKey.slice(0, 5) + '...'"
              type="text"
              disabled
            ></el-input>
            <el-input v-else v-model="livePrivateKey" type="text"></el-input>
          </div>
        </div>
        <div class="mt-4">
          <el-button v-if="hasAllLiveCredentials && !editLive" @click="editLive = true" type="primary">Edit</el-button>
          <el-button v-if="editLive" @click="editLive = false" type="text">Cancel</el-button>
          <el-button v-if="!hasAllLiveCredentials || editLive" @click="saveLiveCredentials" type="primary">Save</el-button>
          <el-button v-if="!editLive && hasAllLiveCredentials" @click="checkLiveMode" :loading="isLoadingLive" type="default"
            >Verify Connection</el-button
          >
        </div>
      </div>

      <div class="pt-4">
        <div class="header flex items-center space-x-2">
          <p v-if="testBadCreds" class="text-lg font-bold text-brand-red flex items-center space-x-2">
            <font-awesome-icon :icon="['fas', 'times-circle']" class="text-sm"></font-awesome-icon>
          </p>
          <p v-else-if="testValidated" class="text-lg font-bold text-green-700 flex items-center space-x-2">
            <font-awesome-icon :icon="['fas', 'check-circle']" class="text-sm"></font-awesome-icon>
          </p>
          <p v-else class="text-lg font-bold text-green-700 flex items-center space-x-2">
            <font-awesome-layers class="text-xl">
              <font-awesome-icon :icon="['fas', 'circle']" class="text-brand-medium-blue" />
              <font-awesome-icon :icon="['fas', 'plug']" class="text-xs text-brand-blue" :class="'text-brand-blue'" />
            </font-awesome-layers>
          </p>
          <span> Sandbox Mode </span>
        </div>
        <div class="grid grid-cols-2 gap-4 max-w-xl mt-2">
          <div>
            <p class="text-sm font-semibold text-gray-700 mb-2">Merchant ID</p>
            <el-input v-if="!editTest && testCredentials.merchantId" :value="testCredentials.merchantId" type="text" disabled></el-input>
            <el-input v-else v-model="testMerchantId" type="text"></el-input>
          </div>
          <div>
            <p class="text-sm font-semibold text-gray-700 mb-2">Public Key</p>
            <el-input v-if="!editTest && testCredentials.publicKey" :value="testCredentials.publicKey" type="text" disabled></el-input>
            <el-input v-else v-model="testPublicKey" type="text"></el-input>
          </div>
          <div class="col-span-2">
            <p class="text-sm font-semibold text-gray-700 mb-2">Private Key</p>
            <el-input
              v-if="!editTest && testCredentials.privateKey"
              :value="testCredentials.privateKey.slice(0, 5) + '...'"
              type="text"
              disabled
            ></el-input>
            <el-input v-else v-model="testPrivateKey" type="text"></el-input>
          </div>
        </div>
        <div class="mt-4">
          <el-button v-if="hasAllTestCredentials && !editTest" @click="editTest = true" type="primary">Edit</el-button>
          <el-button v-if="editTest" @click="editTest = false" type="text">Cancel</el-button>
          <el-button v-if="!hasAllTestCredentials || editTest" @click="saveTestCredentials" type="primary">Save</el-button>
          <el-button v-if="!editTest && hasAllTestCredentials" @click="checkTestMode" :loading="isLoadingTest" type="default"
            >Verify Connection</el-button
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import OrgService from '@/api/org.service';
import { SYNC_ORG } from '@/store/action-types';

export default {
  name: 'BraintreeConnect',
  data() {
    return {
      loading: true,
      isLoadingLive: false,
      isLoadingTest: false,
      // allow editing credentials
      editLive: false,
      editTest: false,
      // for inputs
      testMerchantId: '',
      testPublicKey: '',
      testPrivateKey: '',
      liveMerchantId: '',
      livePublicKey: '',
      livePrivateKey: '',
      testValidated: false,
      liveValidated: false,
      liveBadCreds: false,
      testBadCreds: false,
    };
  },
  async created() {
    try {
      await OrgService.testBraintreeConnect(this.user.org, 'test');
      this.testValidated = true;
    } catch {
      this.testValidated = false;
      if (this.hasAllTestCredentials) {
        this.testBadCreds = true;
      }
    }
    try {
      await OrgService.testBraintreeConnect(this.user.org, 'live');
      this.liveValidated = true;
    } catch {
      this.liveValidated = false;
      if (this.hasAllLiveCredentials) {
        this.liveBadCreds = true;
      }
    }
    this.loading = false;
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    org() {
      return this.$store.state.org;
    },
    testCredentials() {
      if (this.org?.secrets?.test) {
        return {
          merchantId: this.org.secrets.test.braintreeMerchantId,
          publicKey: this.org.secrets.test.braintreePublicKey,
          privateKey: this.org.secrets.test.braintreePrivateKey,
        };
      }
      return {};
    },
    liveCredentials() {
      if (this.org?.secrets?.prod) {
        return {
          merchantId: this.org.secrets.prod.braintreeMerchantId,
          publicKey: this.org.secrets.prod.braintreePublicKey,
          privateKey: this.org.secrets.prod.braintreePrivateKey,
        };
      }
      return {};
    },
    editedLiveCredentials() {
      return this.liveMerchantId || this.livePublicKey || this.livePrivateKey;
    },
    editedTestCredentials() {
      return this.testMerchantId || this.testPublicKey || this.testPrivateKey;
    },
    hasAllLiveCredentials() {
      return this.liveCredentials.merchantId && this.liveCredentials.publicKey && this.liveCredentials.privateKey;
    },
    hasAllTestCredentials() {
      return this.testCredentials.merchantId && this.testCredentials.publicKey && this.testCredentials.privateKey;
    },
  },
  methods: {
    deactivate() {
      this.$notify({ title: 'Deactivation Queued', type: 'warning' });
    },
    saveLiveCredentials() {
      this.saveBraintreeSecrets('live');
    },
    saveTestCredentials() {
      this.saveBraintreeSecrets('test');
    },
    async saveBraintreeSecrets(mode) {
      try {
        const secrets = {};
        this.loading = true;
        if (this.liveMerchantId && mode === 'live') {
          secrets.prod = {
            braintreeMerchantId: this.liveMerchantId,
            braintreePublicKey: this.livePublicKey,
            braintreePrivateKey: this.livePrivateKey,
          };
        }
        if (this.testMerchantId && mode === 'test') {
          secrets.test = {
            braintreeMerchantId: this.testMerchantId,
            braintreePublicKey: this.testPublicKey,
            braintreePrivateKey: this.testPrivateKey,
          };
        }
        const result = await OrgService.saveBraintreeSecrets(this.user.org, secrets);
        this.$notify({ title: 'Braintree Credentials Saved', type: 'success' });
        await this.$store.dispatch(SYNC_ORG);
        if (mode === 'live') {
          this.editLive = false;
        } else {
          this.editTest = false;
        }
      } catch (error) {
        console.log(error);
        this.$notify({ title: 'Braintree Credentials Update Failed', type: 'error' });
      } finally {
        this.loading = false;
      }
    },
    async checkTestMode() {
      this.isLoadingTest = true;
      try {
        await OrgService.testBraintreeConnect(this.user.org, 'test');
        this.$notify({ title: 'Test Mode Connected', type: 'success' });
        this.testValidated = true;
        this.testBadCreds = false;
      } catch (error) {
        this.$notify({ title: 'Error', message: 'Braintree Test Connection Failed', type: 'error' });
        this.testValidated = false;
        this.testBadCreds = true;
        console.log('Error', error);
      }
      this.isLoadingTest = false;
    },
    async checkLiveMode() {
      this.isLoadingLive = true;
      try {
        await OrgService.testBraintreeConnect(this.user.org, 'live');
        this.liveValidated = true;
        this.liveBadCreds = false;
        this.$notify({ title: 'Live Mode Connected', type: 'success' });
      } catch (error) {
        this.$notify({ title: 'Error', message: 'Braintree Live Connection Failed', type: 'error' });
        console.log('Error', error);
        this.liveValidated = false;
        this.liveBadCreds = true;
      }
      this.isLoadingLive = false;
    },
  },
};
</script>
